<template>
	<div class="page usage-page">
		<ErrorEmptyState
			v-if="loadError"
			title="Could not load your Usage Data"
			message="Often this is a temporary network or connectivity issue."
			:retrying="retryingFetch"
			@retry="retryFetchUsage"
		/>
		<template v-else>
			<div
				v-if="exceededCategories.length > 0"
				role="alert"
				class="sgnl-alert sgnl-text-color-secondary"
				:class="`sgnl-alert--${bannerSeverity}`"
			>
				<div
					class="snackbar-icon-container"
					:class="`snackbar-${bannerSeverity}`"
				>
					<Warning16 />
				</div>
				<div class="sgnl-alert__content">
					<span class="sgnl-paragraph-m-medium sgnl-alert__title">{{ limitBannerTitle }}</span>
					<span class="sgnl-paragraph-s-regular">
						{{ limitBannerBody }}
					</span>
					<v-btn
						v-if="showUpgradeButton"
						variant="flat"
						color="primary"
						class="sgnl-align-self-start"
						:to="{ path: '/billing' }"
					>
						<span>Upgrade</span>
						<Upgrade16 />
					</v-btn>
				</div>
			</div>
			<div class="usage-grid">
				<div class="grid-user-summary">
					<UserUsageAllowanceCard />
				</div>

				<div class="grid-usage-history">
					<v-card
						variant="outlined"
						height="100%"
						min-height="250px"
					>
						<UsageLineChart />
					</v-card>
				</div>

				<section class="grid-usage-detail">
					<ErrorEmptyState
						v-if="tierUnavailable"
						title="Could not load your Usage Data"
						message="Often this is a temporary network or connectivity issue."
						:retrying="retryingFetch"
						@retry="retryFetchUsage"
					/>
					<div
						v-else
						class="usage-card-grid"
					>
						<UsageDetailCard
							title="Compute"
							:iconSrc="computeIcon"
							:subtitle="usageCardSubtitle"
							:usageQuotas="computeUsage"
							:tier-resolved="currentUserTierDetails !== undefined"
						/>
						<UsageDetailCard
							title="Connections"
							:subtitle="{ default: '', warning: 'Upgrade to connect more resources.' }"
							:iconSrc="connectionsIcon"
							:usageQuotas="connectionsUsage"
							:tier-resolved="currentUserTierDetails !== undefined"
						/>
						<UsageDetailCard
							title="Storage"
							:subtitle="{ default: '', warning: 'Upgrade to get more storage and drives.' }"
							:iconSrc="storageIcon"
							:usageQuotas="storageUsage"
							:tier-resolved="currentUserTierDetails !== undefined"
						/>
						<UsageDetailCard
							title="API"
							:iconSrc="apiIcon"
							:subtitle="{
								default: '',
								warning: 'Upgrade to get more storage and drives.',
								error: 'Upgrade to enable API access to Signaloid Cloud',
							}"
							:usageQuotas="apiUsage"
							:tier-resolved="currentUserTierDetails !== undefined"
						/>
					</div>
				</section>
			</div>
		</template>
	</div>
</template>

<script lang="ts">
/* eslint-disable vue/multi-word-component-names */
import { defineComponent } from "vue";

// Components
import UsageLineChart from "@/components/Home/UsageLineChart.vue";
import UsageDetailCard from "@/components/Usage/UsageDetailCard.vue";
import UserUsageAllowanceCard from "@/components/Usage/UserUsageAllowanceCard.vue";
import ErrorEmptyState from "@/components/Common/ErrorEmptyState.vue";

// Icons
import Warning16 from "@carbon/icons-vue/es/warning/16";
import Upgrade16 from "@carbon/icons-vue/es/upgrade/16";
import computeIcon from "@/assets/icons/usage/compute.svg";
import connectionsIcon from "@/assets/icons/usage/connections.svg";
import storageIcon from "@/assets/icons/usage/storage.svg";
import apiIcon from "@/assets/icons/usage/api.svg";

// Libraries
import moment from "moment";

// Stores
import { mapState } from "pinia";
import { useDataSourcesStore } from "@/stores/dataSources";
import { useUserStore } from "@/stores/user";
import { useCoresStore } from "@/stores/cores";
import { useRepositoriesStore } from "@/stores/repositories";
import { ResolvedTierDetail, RESETTING_LIMITS, TierLimitDisplayObject, UserLimitsE } from "@/js/tierconfig";
import { UsageCategoryName, buildLimitBannerBody, buildLimitBannerTitle } from "@/js/usageLimitBannerCopy";
import { UserResourceUsage } from "@/types/user";

function isQuotaExceeded(quota: TierLimitDisplayObject): boolean {
	return quota.limit > 0 && quota.usage >= quota.limit;
}

type ComponentData = {
	usageHistoryAvailable: boolean;
	computeUsage: TierLimitDisplayObject[];
	storageUsage: TierLimitDisplayObject[];
	connectionsUsage: TierLimitDisplayObject[];
	apiUsage: TierLimitDisplayObject[];
	usageResetDay: undefined | moment.Moment;
	loadError: boolean;
	retryingFetch: boolean;
};

export default defineComponent({
	name: "Usage",
	components: { UsageDetailCard, UsageLineChart, UserUsageAllowanceCard, ErrorEmptyState, Warning16, Upgrade16 },
	data: (): ComponentData => ({
		usageHistoryAvailable: false,
		computeUsage: [
			{
				name: "Dynamic Instructions",
				id: UserLimitsE.DynamicInstructionCount,
				usage: 0,
				limit: 0,
				unit: "",
				quotaSuffix: "",
			},
			{
				name: "Concurrent Tasks",
				id: UserLimitsE.ConcurrentTaskCount,
				usage: 0,
				limit: 0,
				unit: "",
				quotaSuffix: "",
			},
			{
				name: "Concurrent Builds",
				id: UserLimitsE.ConcurrentBuildCount,
				usage: 0,
				limit: 0,
				unit: "",
				quotaSuffix: "",
			},
			{
				name: "Custom Cores",
				id: UserLimitsE.CoreCount,
				usage: 0,
				limit: 0,
				unit: "",
				quotaSuffix: "",
			},
			{
				name: "Distribution Plots",
				id: UserLimitsE.PlotCount,
				usage: 0,
				limit: 0,
				unit: "",
				quotaSuffix: "",
			},
		],
		storageUsage: [
			{
				name: "Cloud Storage",
				id: UserLimitsE.CloudStorageBytes,
				usage: 0,
				limit: 0,
				unit: "B",
			},
			{
				name: "Build Registry Storage",
				id: UserLimitsE.RegistryStorageBytesMaximum,
				usageId: "RegistryStorageBytes",
				usage: 0,
				limit: 0,
				unit: "B",
			},
			{
				name: "Data Drives",
				id: UserLimitsE.DataDriveCount,
				usage: 0,
				limit: 0,
				unit: "",
				quotaSuffix: "",
			},
		],
		connectionsUsage: [
			{
				name: "Repositories",
				id: UserLimitsE.RepositoryCount,
				usage: 0,
				limit: 0,
				unit: "",
				quotaSuffix: "",
			},
			{
				name: "Sensor Gateways",
				id: UserLimitsE.GatewayCount,
				usage: 0,
				limit: 0,
				unit: "",
				quotaSuffix: "",
			},
			{
				name: "S3 Buckets",
				id: UserLimitsE.BucketCount,
				usage: 0,
				limit: 0,
				unit: "",
				quotaSuffix: "",
			},
			{
				name: "Data Sources",
				id: UserLimitsE.DataSourceCount,
				usage: 0,
				limit: 0,
				unit: "",
				quotaSuffix: "",
			},
		],
		apiUsage: [
			{
				name: "API Requests",
				id: UserLimitsE.APIAccessCount,
				usage: 0,
				limit: 0,
				unit: "",
				quotaSuffix: "",
			},
			{
				name: "API Keys",
				id: UserLimitsE.KeyCount,
				usage: 0,
				limit: 0,
				unit: "",
				quotaSuffix: "",
			},
		],
		usageResetDay: undefined,
		loadError: false,
		retryingFetch: false,
	}),
	setup() {
		// Note: gets called after beforeCreate in vue2
		const dataSourcesStore = useDataSourcesStore();
		const userStore = useUserStore();
		const repositoriesStore = useRepositoriesStore();
		return { dataSourcesStore, userStore, repositoriesStore, computeIcon, connectionsIcon, storageIcon, apiIcon };
	},
	computed: {
		usageResetDayString(): string {
			return this.usageResetDay && this.usageResetDay.isValid() ? this.usageResetDay.fromNow() : "";
		},
		usageResetDate(): string {
			return this.usageResetDay && this.usageResetDay.isValid() ? this.usageResetDay.format("MMM D") : "";
		},
		exceededQuotas(): TierLimitDisplayObject[] {
			return [...this.computeUsage, ...this.connectionsUsage, ...this.storageUsage, ...this.apiUsage].filter(
				isQuotaExceeded,
			);
		},
		exceededCategories(): UsageCategoryName[] {
			const categories: { name: UsageCategoryName; quotas: TierLimitDisplayObject[] }[] = [
				{ name: "Compute", quotas: this.computeUsage },
				{ name: "Connections", quotas: this.connectionsUsage },
				{ name: "Storage", quotas: this.storageUsage },
				{ name: "API", quotas: this.apiUsage },
			];
			return categories.filter(({ quotas }) => quotas.some(isQuotaExceeded)).map(({ name }) => name);
		},
		hasResettingExceedance(): boolean {
			return this.exceededQuotas.some((v) => RESETTING_LIMITS.has(v.id));
		},
		hasNonResettingExceedance(): boolean {
			return this.exceededQuotas.some((v) => !RESETTING_LIMITS.has(v.id));
		},
		resetDateString(): string {
			if (this.usageResetDay && this.usageResetDay.isValid() && this.usageResetDay.isSameOrAfter(moment())) {
				return this.usageResetDay.format("MMM D, YYYY");
			}
			return "";
		},
		limitBannerTitle(): string {
			return buildLimitBannerTitle(this.exceededCategories);
		},
		limitBannerBody(): string {
			return buildLimitBannerBody({
				hasResettingExceedance: this.hasResettingExceedance,
				hasNonResettingExceedance: this.hasNonResettingExceedance,
				resetDateLabel: this.resetDateString,
			});
		},
		showUpgradeButton(): boolean {
			return this.currentUserTierCode !== "enterprise-tier-l";
		},
		tierUnavailable(): boolean {
			return this.tierResolutionFailed;
		},
		bannerSeverity(): "error" | "warning" {
			return this.hasResettingExceedance ? "error" : "warning";
		},
		usageCardSubtitle(): { default: string; warning: string } {
			if (this.usageResetDay && this.usageResetDay.isValid() && this.usageResetDay?.isSameOrAfter(moment())) {
				return {
					default: `Usage quota resets ${this.usageResetDay.fromNow()} on ${this.usageResetDate}.`,
					warning: `Usage resets ${this.usageResetDay.fromNow()}. Upgrade to increase your monthly quota.`,
				};
			} else {
				return {
					default: "Upgrade to increase your monthly quota.",
					warning: "Upgrade to increase your monthly quota.",
				};
			}
		},
		...mapState(useCoresStore, {
			localCoresList: "localCoresList",
		}),
		...mapState(useDataSourcesStore, {
			driveList: "driveList",
			bucketList: "bucketList",
			gatewayList: "gatewayList",
		}),
		...mapState(useUserStore, {
			user: "currentUser",
			currentUserResourceUsage: "currentUserResourceUsage",
			currentUserTierDetails: "currentUserTierDetails",
			currentUserTierCode: "currentUserTierCode",
			tierResolutionFailed: "tierResolutionFailed",
		}),
	},
	watch: {
		currentUserResourceUsage: {
			handler: function (newUsage: UserResourceUsage) {
				if (newUsage) {
					this.usageResetDay = moment(newUsage.ResetsAt * 1000);
					this.computeUsage = this.computeUsage.map((v: TierLimitDisplayObject) => {
						v.usage = newUsage[v.usageId ?? v.id] ?? v.usage;
						return v;
					});
					this.storageUsage = this.storageUsage.map((v: TierLimitDisplayObject) => {
						v.usage = newUsage[v.usageId ?? v.id] ?? v.usage;
						return v;
					});
					this.connectionsUsage = this.connectionsUsage.map((v: TierLimitDisplayObject) => {
						v.usage = newUsage[v.usageId ?? v.id] ?? v.usage;
						return v;
					});
					this.apiUsage = this.apiUsage.map((v: TierLimitDisplayObject) => {
						v.usage = newUsage[v.usageId ?? v.id] ?? v.usage;
						return v;
					});
				}
			},
			deep: true,
			immediate: true,
		},
		driveList: {
			handler: function (newValue) {
				this.storageUsage[this.storageUsage.findIndex((v) => v.id == UserLimitsE.DataDriveCount)].usage =
					newValue.length;
			},
			immediate: true,
		},
		gatewayList: {
			handler: function (newValue) {
				this.connectionsUsage[this.connectionsUsage.findIndex((v) => v.id == UserLimitsE.GatewayCount)].usage =
					newValue.length;
			},
			immediate: true,
		},
		bucketList: {
			handler: function (newValue) {
				this.connectionsUsage[this.connectionsUsage.findIndex((v) => v.id == UserLimitsE.BucketCount)].usage =
					newValue.length;
			},
			immediate: true,
		},
		currentUserTierDetails: {
			handler: function (newLimits: undefined | ResolvedTierDetail) {
				if (newLimits) {
					const allowance = newLimits.allowance;

					this.computeUsage = this.computeUsage.map((v) => {
						v.limit = allowance[v.id];
						return v;
					});

					this.storageUsage = this.storageUsage.map((v) => {
						v.limit = allowance[v.id];
						return v;
					});

					this.connectionsUsage = this.connectionsUsage.map((v) => {
						v.limit = allowance[v.id];
						return v;
					});
					this.apiUsage = this.apiUsage.map((v) => {
						v.limit = allowance[v.id];
						return v;
					});
				}
			},
			deep: true,
			immediate: true,
		},
	},

	methods: {
		async loadUsage() {
			this.loadError = false;
			try {
				await this.userStore.fetchCurrentUserObjectFromDB();
			} catch {
				this.loadError = true;
				return;
			}
			await this.userStore.getCurrentUserTierDetails();
			await this.dataSourcesStore.fetchAllDataSources();
			if (this.dataSourcesStore.loadError) {
				this.loadError = true;
			}
		},
		async retryFetchUsage() {
			this.retryingFetch = true;
			try {
				await this.loadUsage();
			} finally {
				this.retryingFetch = false;
			}
		},
	},

	async created() {
		await this.loadUsage();
	},
});
</script>

<style lang="scss" scoped>
.usage-page {
	display: flex;
	flex-direction: column;
	gap: var(--sgnl-space-16);
}
.usage-grid {
	display: grid;
	gap: var(--sgnl-space-16);
	align-items: top;

	grid-template-columns: minmax(0, 1fr);
	grid-template-rows: auto;
	grid-template-areas:
		"user-summary"
		"usage-history"
		"usage-detail";
}
@media (min-width: map-get($grid-breakpoints,lg)) {
	.usage-grid {
		grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
		grid-template-areas:
			"user-summary usage-history"
			"usage-detail usage-detail";
	}
}
.grid-usage-history {
	grid-area: usage-history;
}
.grid-user-summary {
	grid-area: user-summary;
}
.grid-usage-detail {
	grid-area: usage-detail;
	display: grid;
}

.usage-card-grid {
	display: grid;
	gap: var(--sgnl-space-16);
	grid-template-columns: repeat(2, minmax(0, 1fr));
}
@media (max-width: 599px) {
	.usage-card-grid {
		grid-template-columns: minmax(0, 1fr);
	}
}
</style>
